import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { IImportFileSchema, importFileSchema } from '@/api/testCase';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Modal, Dialog } from 'alurkerja-ui';
import { MdCloudUpload } from 'react-icons/md';
import { TestIssueService } from '@/api/testIssue';
import { FormContainer } from '@/components/form/FormContainer';
import { UploadField } from '@/components/form/fields/UploadField';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import axios from 'axios';

type ImportErrorItem = {
  row: number | '-';
  column: string;
  message: string;
};

type ImportErrorResponse = {
  message?: string;
  errors?: ImportErrorItem[];
};

type ImportTestIssueProps = {
  project_id?: string;
};

export const ImportTestIssue: React.FC<ImportTestIssueProps> = ({
  project_id,
}) => {

  const [importErrors, setImportErrors] = useState<ImportErrorItem[]>([]);

  const form = useForm<IImportFileSchema>({
    resolver: zodResolver(importFileSchema),
  });

  const { mutate: importMutate, isPending: isImportLoading } =
    TestIssueService.useImport(project_id!);

  const submitAction = (data: IImportFileSchema) => {
    setImportErrors([]);

    importMutate(data, {
      onSuccess: () => {
        Dialog.success({
          title: 'Berhasil',
          description: 'Berhasil Import Issue',
          callback: () => window.location.reload(),
        });
      },

      onError: (error: unknown) => {
        if (axios.isAxiosError(error)) {
          const status = error.response?.status;
          const responseData = error.response?.data as
            | ImportErrorResponse
            | undefined;

          if (
            status === 422 &&
            responseData?.errors &&
            responseData.errors.length > 0
          ) {
            setImportErrors(responseData.errors);
            return;
          }

          const message = responseData?.message;

          if (status === 422 && typeof message === 'string') {
            const match = message.match(/^Invalid data: (.+)$/);

            if (match) {
              const columnNames: Record<string, string> = {
                priority: 'Priority',
                severity: 'Severity',
                status: 'Status',
                category: 'Category',
                issue_type: 'Issue Type',
                release_id: 'Release',
                module_id: 'Module',
              };

              const column = columnNames[match[1]] ?? match[1];

              Dialog.error({
                title: 'Gagal',
                description: `Kolom ${column} tidak boleh kosong atau memiliki nilai yang tidak valid.`,
              });
              return;
            }

            Dialog.error({
              title: 'Gagal',
              description: message,
            });
            return;
          }

          if (status === 500) {
            Dialog.error({
              title: 'Gagal',
              description: 'Terjadi kesalahan saat import data.',
            });
            return;
          }
        }

        Dialog.error({
          title: 'Gagal',
          description: 'Gagal Import Issue',
        });
      },
    });
  };

  return (
    <Modal
      title="Import Issue"
      maxWidth={importErrors.length > 0 ? '5xl' : '3xl'}
      triggerButton={
        <OutlineButton icon={<MdCloudUpload />}>Import</OutlineButton>
      }
    >
      <div className="m-5">
        {importErrors.length > 0 ? (
          <div className="mt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-red-500 font-bold text-lg">
                Preview Error Import Data
              </h3>

              <Button onClick={() => setImportErrors([])} size="small">
                Upload Ulang
              </Button>
            </div>

            <div className="overflow-x-auto max-h-[60vh]">
              <table className="w-full text-sm text-left text-gray-700 border">
                <thead className="text-xs uppercase bg-red-100 text-red-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 border font-semibold">No</th>
                    <th className="px-4 py-3 border font-semibold">Pesan</th>
                    <th className="px-4 py-3 border font-semibold">Baris</th>
                    <th className="px-4 py-3 border font-semibold">Kolom</th>
                  </tr>
                </thead>

                <tbody>
                  {importErrors.map((err, idx) => (
                    <tr
                      key={idx}
                      className="bg-white border-b hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 border">{idx + 1}</td>
                      <td className="px-4 py-3 border text-red-600 font-medium">
                        {err.message}
                      </td>
                      <td className="px-4 py-3 border font-bold">
                        Baris {err.row}
                      </td>
                      <td className="px-4 py-3 border">{err.column}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <FormContainer form={form} onSubmit={submitAction}>
            <UploadField
              name="file"
              label="Attachments"
              note="Allowed Formats: xlsx"
              type="file"
              allowedExtensions={['xlsx']}
              hasRequiredAsterisk
            />

            <Button className="mt-3" loading={isImportLoading}>
              Import
            </Button>
          </FormContainer>
        )}
      </div>
    </Modal>
  );
};
